/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Nov 30 2023
 **/

import { useTheme } from "@emotion/react";
import React, { useState } from "react";

import { Chip, Column, Row, Typography } from "@/frameworks/designStuff/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { useOutsideClickEffect } from "@/frameworks/libraries/hooks";

import { SearchBar } from "../SearchBar";

export default function SearchBarWithHistory({
	searchKey,
	onSearch,
	onClearList,
	onClearText,
	onChange,
	autoFocus = true
}: {
	searchKey: string;
	onSearch: (
		ev: React.MouseEvent<HTMLParagraphElement> | React.FormEvent<HTMLFormElement>,
		searchValue?: string
	) => void;
	onClearList?: () => void;
	onClearText?: () => void;
	onChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
	autoFocus?: boolean;
}) {
	const theme = useTheme();
	const [recentSearchList, setRecentSearchList] = useState(Configure.recentSearchList[searchKey]);
	const [searchValue, setSearchValue] = useState("");
	const [isOpen, setIsOpen] = useState(false);

	const { ref } = useOutsideClickEffect<HTMLFormElement>(() => {
		setIsOpen(false);
	});

	const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
		if (!isOpen) {
			setIsOpen(true);
		}
		setSearchValue(ev.target.value);
		onChange?.(ev);
	};

	const removeRecentSearchText = (index: number) => {
		const searchList = Configure.recentSearchList[searchKey];
		if (!searchList || !recentSearchList) return;
		Configure.recentSearchList = {
			...Configure.recentSearchList,
			[searchKey]: [...searchList].filter((_, _index) => index !== _index)
		};
		setRecentSearchList([...recentSearchList].filter((_, _index) => index !== _index));
	};

	const removeAllRecentSearchList = () => {
		Configure.recentSearchList = {
			...Configure.recentSearchList,
			[searchKey]: null
		};
		setRecentSearchList(null);
		onClearList?.();
	};

	const handleSearch = (ev: React.MouseEvent<HTMLParagraphElement> | React.FormEvent<HTMLFormElement>) => {
		ev.preventDefault();
		const recentSearchList = Configure.recentSearchList[searchKey];
		let value = searchValue;

		const target = ev.currentTarget;

		if (target instanceof HTMLParagraphElement) {
			value = target.dataset.value as string;
			setSearchValue(value);
		}

		onSearch(ev, value);

		if (value.trim() === "") return;

		if (!recentSearchList) {
			Configure.recentSearchList = {
				...Configure.recentSearchList,
				[searchKey]: [value]
			};
		} else {
			Configure.recentSearchList = {
				...Configure.recentSearchList,
				[searchKey]: [...new Set([value, ...recentSearchList.slice(0, 9)])]
			};
		}

		// @ts-ignore
		setRecentSearchList((prev: unknown[]) => {
			if (!prev) return [value];
			return [...new Set([value, ...prev.slice(0, 9)])];
		});

		setIsOpen(false);
	};

	const clearText = () => {
		setSearchValue("");
		onClearText?.();
	};

	return (
		<Column.form onSubmit={handleSearch} ref={ref} css={{ width: "100%" }}>
			<SearchBar
				autoFocus={autoFocus}
				value={searchValue}
				placeholder={_T("검색")}
				onFocus={() => {
					setIsOpen(true);
				}}
				onClear={clearText}
				onChange={handleChange}
				dropdownContent={
					isOpen &&
					Configure.recentSearchList[searchKey] && (
						<Column css={{ width: "100%", padding: "6px" }} gap={14}>
							<Row css={{ width: "100%" }} horizonAlign="distribute">
								<Typography css={{ color: theme.colors.neutral_60_100 }} typoSize="Body4">
									{_T("최근 검색어")}
								</Typography>
								<Row gap={12} verticalAlign="center">
									<Typography
										typoSize="Body4"
										onClick={removeAllRecentSearchList}
										style={{ cursor: "pointer" }}
									>
										{_T("전체 삭제")}
									</Typography>
									<div style={{ borderLeft: "1px solid", height: "12px" }} />
									<Typography
										typoSize="Body4"
										style={{ cursor: "pointer" }}
										onClick={(ev) => {
											ev.preventDefault();
											setIsOpen(false);
										}}
									>
										{_T("닫기")}
									</Typography>
								</Row>
							</Row>
							<Row css={{ width: "100%", flexWrap: "wrap" }} gap={8}>
								{recentSearchList?.map((recentSearchText: unknown, index: number) => (
									<Chip
										key={index}
										colorTheme="gray"
										handleClose={() => removeRecentSearchText(index)}
									>
										<p onClick={handleSearch} data-value={recentSearchText}>
											{recentSearchText as string}
										</p>
									</Chip>
								))}
							</Row>
						</Column>
					)
				}
			/>
		</Column.form>
	);
}
